import { useState, FormEvent } from 'react';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    console.log('🚀 Form submission started');
    console.log('📝 Form data:', formData);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/contact-form`;

      console.log('🔗 Function URL:', functionUrl);

      // Create FormData object to match Edge Function expectations
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      console.log('📤 Sending request to Edge Function...');

      const response = await fetch(functionUrl, {
        method: 'POST',
        body: formDataToSend
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response ok:', response.ok);

      const responseText = await response.text();
      console.log('📥 Response body:', responseText);

      if (response.ok) {
        console.log('✅ Form submitted successfully');
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        console.error('❌ Form submission failed:', responseText);
        setSubmitStatus('error');
        setErrorMessage(`Server error: ${response.status} - ${responseText}`);
      }
    } catch (error) {
      console.error('❌ Form submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 bg-slate-900">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-3 sm:mb-4">
          Contact Us
        </h2>
        <p className="text-base sm:text-lg text-slate-400 text-center mb-12 sm:mb-16 max-w-2xl mx-auto px-4">
          Schedule a consultation or reach out with any questions. We're here to help you on your journey to recovery.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 sm:p-8 mb-6 sm:mb-8 border border-white/5">
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6">Get In Touch</h3>
              
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <h4 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Contact for Appointments & Inquiries</h4>
                  <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-3 sm:mb-4">
                    We are happy to hear from you! Please use the following contact details to get in touch for any appointments or inquiries.
                  </p>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-500/20 rounded-full flex-shrink-0">
                    <i className="ri-phone-line text-xl sm:text-2xl text-emerald-400"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1 text-sm sm:text-base">Phone</h4>
                    <a href="tel:818-221-8000" className="text-sm sm:text-base text-slate-300 hover:text-emerald-400 transition-colors cursor-pointer">
                      (818) 221-8000
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-500/20 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-xl sm:text-2xl text-emerald-400"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1 text-sm sm:text-base">Email</h4>
                    <a href="mailto:contact@neurotraumamg.com" className="text-sm sm:text-base text-slate-300 hover:text-emerald-400 transition-colors cursor-pointer break-all">
                      contact@neurotraumamg.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 sm:p-8 border border-white/5">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6">Send Us a Message</h3>
            
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-5 sm:mb-6">
              Have questions or ready to schedule an appointment? Dr. Yousefi is here to assist. Simply submit your contact details, and our patient coordinators will reach out to guide you through the appointment process, ensuring Dr. Yousefi can help you on your journey to a pain-free life.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-white/10 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm text-white placeholder-slate-400"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-white/10 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm text-white placeholder-slate-400"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-white/10 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-sm text-white placeholder-slate-400"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-white/10 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all resize-none text-sm text-white placeholder-slate-400"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 text-white px-5 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-emerald-400 transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-400/40 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm">
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-rose-500/20 border border-rose-500/30 text-rose-300 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm">
                    {errorMessage || 'Sorry, there was an error sending your message. Please try again or call us directly.'}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}